import { __decorate } from "tslib";
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { numeral } from '@holmescorp/numeral';
import Button from '@dojo/widgets/button';
import { logQuiz } from '@holmescorp/logger';
import { setInreview, setIsaudit, setIsAutoStart } from '../../support/state';
import { BaseProgression } from '../base';
import css from '../../css/questionfeedback.m.css';
import { convertToBoolean } from '@holmescorp/common';
import { formatAriaProperties } from '@dojo/widgets/common/util';
const isEmpty = (obj) => Object.keys(obj).length === 0;
let QFAuditBase = class QFAuditBase extends BaseProgression {
    beginQuiz(quizid, use_restart = false) {
        logQuiz.info(`begin quiz ${quizid}`);
        const { svc } = this;
        const { quizstore } = this.properties;
        setIsaudit(quizstore)({ isaudit: false });
        setInreview(quizstore)({ inreview: false });
        if (use_restart) {
            svc.continue_quiz().then((v) => {
                this.startquiz = v;
                this.quiz_startup();
            });
        }
        else {
            svc.start_quiz().then((v) => {
                this.startquiz = v;
                this.quiz_startup();
            });
        }
    }
    buttons() {
        const { quiz = {}, quizstore, info, isrunning, theme, localizedBundle: { messages: { continue: continueText = 'Continue', start_over: restartText = 'Start Over', begin: beginText = 'Begin', review_last_attempt: reviewText = '', hide_begin_on_quiz_end = 'none' } } } = this.properties;
        const { use_restart } = quiz;
        const { svc } = this;
        const quizid = quiz.id;
        const noBeginOnFinal = isrunning === 'final' && hide_begin_on_quiz_end !== 'none';
        logQuiz.info(`quizid = ${quizid}; isrunning = ${isrunning}`);
        if (use_restart) {
            return [
                v('div', { xqa: 'action continue', classes: [this.theme(css.oneaction)] }, [
                    w(Button, {
                        theme,
                        key: 'continue',
                        onClick: () => {
                            if (!this._inAction) {
                                this._inAction = true;
                                logQuiz.info(`continue quiz ${quizid}`);
                                setIsaudit(quizstore)({ isaudit: false });
                                setInreview(quizstore)({ inreview: false });
                                svc.continue_quiz().then((v) => {
                                    this.startquiz = v;
                                    this.quiz_startup();
                                });
                            }
                        }
                    }, [continueText])
                ]),
                v('div', { xqa: 'action restart', classes: [this.theme(css.oneaction)] }, [
                    w(Button, {
                        theme,
                        key: 'restart',
                        onClick: () => {
                            if (!this._inAction) {
                                this._inAction = true;
                                logQuiz.info(`restart quiz ${quizid}`);
                                setIsaudit(quizstore)({ isaudit: false });
                                setInreview(quizstore)({ inreview: false });
                                svc.restart_quiz().then((v) => {
                                    this.startquiz = v;
                                    this.quiz_startup();
                                });
                            }
                        }
                    }, [restartText])
                ])
            ];
        }
        else {
            const lastresult = info && info.lastresult;
            const hasLastresult = lastresult && !isEmpty(lastresult);
            return [
                noBeginOnFinal
                    ? null
                    : v('div', { xqa: 'action begin', classes: [this.theme(css.oneaction)] }, [
                        w(Button, {
                            theme,
                            key: 'begin',
                            onClick: () => {
                                if (!this._inAction) {
                                    this._inAction = true;
                                    logQuiz.info(`begin quiz ${quizid}`);
                                    setIsaudit(quizstore)({ isaudit: false });
                                    setInreview(quizstore)({ inreview: false });
                                    svc.start_quiz().then((v) => {
                                        this.startquiz = v;
                                        this.quiz_startup();
                                    });
                                }
                            }
                        }, [beginText])
                    ]),
                hasLastresult && !quiz.attempts_noaudit
                    ? w(Button, {
                        theme,
                        key: 'audit',
                        onClick: () => {
                            if (!this._inAction) {
                                this._inAction = true;
                                this.audit_init();
                                const { auditsvc } = this;
                                logQuiz.info(`audit quiz ${quizid}`);
                                setIsaudit(quizstore)({ isaudit: true });
                                setInreview(quizstore)({ inreview: false });
                                auditsvc.start_quiz().then(() => {
                                    this.startquiz = { cursor: 0, status: 'feedback' };
                                    this.audit_startup();
                                });
                            }
                        }
                    }, [reviewText])
                    : null
            ];
        }
    }
    run_quiz_end() {
        const { localizedBundle: { messages: { score: scoreText = '' } }, quiz = {}, info, quizstore } = this.properties;
        const lastresult = info && info.lastresult;
        const hasLastresult = lastresult && !isEmpty(lastresult);
        const percent = hasLastresult ? lastresult.totalpercentcorrect * 0.01 : 0;
        const { instructions_done } = quiz;
        return [
            hasLastresult ? w('QuizModuleProgress', { quizstore }) : null,
            v('div', {
                key: 'instructions',
                id: 'quiz-instructions',
                classes: [this.theme(css.instructions)],
                innerHTML: instructions_done
            }),
            hasLastresult && !quiz.attempts_noscore
                ? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
                    v('span', { key: 'score-title', classes: [this.theme(css.scoreTitle)] }, [`${scoreText} `]),
                    v('span', { key: 'score-percent', classes: [this.theme(css.scorePercent)] }, [
                        `${numeral(percent).format('0%')}`
                    ])
                ])
                : v('div', { key: 'score', xqa: 'last-quiz-score' }),
            v('div', {
                key: 'quiz-actions',
                classes: [this.theme(css.actions)],
                role: 'group',
                ...formatAriaProperties({ labelledby: 'quiz-instructions' })
            }, this.buttons())
        ];
    }
    run_quiz_begin() {
        const { localizedBundle: { messages: { last_score: lastScoreText = '', use_quiz_autostart = 'false' } }, quiz = {}, info, quizstore } = this.properties;
        const lastresult = info && info.lastresult;
        const hasLastresult = lastresult && !isEmpty(lastresult);
        const percent = hasLastresult ? lastresult.totalpercentcorrect * 0.01 : 0;
        const { instructions, use_restart } = quiz;
        const autostart = convertToBoolean(use_quiz_autostart);
        const isStarted = quizstore.get(quizstore.path('isautostart'));
        if (autostart) {
            if (isStarted) {
                return [];
            }
            this.beginQuiz(quiz.id, use_restart);
            setIsAutoStart(quizstore)({ isautostart: true });
            return [];
        }
        return [
            hasLastresult ? w('QuizModuleProgress', { quizstore }) : null,
            v('div', {
                key: 'instructions',
                id: 'quiz-instructions',
                classes: [this.theme(css.instructions)],
                innerHTML: instructions
            }),
            hasLastresult && !quiz.attempts_noscore
                ? v('div', { key: 'score', classes: [this.theme(css.score)], xqa: 'last-quiz-score' }, [
                    v('span', { key: 'score-title', classes: [this.theme(css.scoreTitle)] }, [`${lastScoreText} `]),
                    v('span', { key: 'score-percent', classes: [this.theme(css.scorePercent)] }, [
                        `${numeral(percent).format('0%')}`
                    ])
                ])
                : v('div', { key: 'score', xqa: 'last-quiz-score' }),
            v('div', {
                key: 'quiz-actions',
                classes: [this.theme(css.actions)],
                role: 'group',
                ...formatAriaProperties({ labelledby: 'quiz-instructions' })
            }, this.buttons())
        ];
    }
    render() {
        return null;
    }
};
QFAuditBase = __decorate([
    theme(css)
], QFAuditBase);
export { QFAuditBase };
